import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Input } from "../../components/ui/input2";
import { Button } from "../../components/ui/Button";
import { Zap, Info } from "lucide-react";
import SectionHeader from "../../components/ui/SectionHeader";
import { SettingsOutletContextType } from "../Settings";
import { updateXonderSetting } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";
import { useAuth } from "../../auth/AuthContext";

const XONDERSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } =
    useOutletContext<SettingsOutletContextType>();
  const { syncSettingsToLocalStorage } = useAuth();
  const [xonderId, setXonderId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const settings = settingsData?.settings;
    if (settings) {
      // Load from stripe_id since backend stores xonder_id in stripe_id field
      const xonderIdValue = settings.stripe_id || "";
      console.log("XONDER Settings - Loading from stripe_id:", xonderIdValue);
      setXonderId(xonderIdValue);
    } else {
      console.log("XONDER Settings - No settings found in settingsData:", settingsData);
    }
  }, [settingsData]);

  const handleSave = async () => {
    if (!xonderId.trim()) return;

    setSaving(true);
    try {
      const response = await updateXonderSetting({
        xonder_id: xonderId,
      });

      if (response.success) {
        toast.success("XONDER settings updated successfully");
        // Refresh settings to get the updated value from backend
        await refreshSettings();
        await syncSettingsToLocalStorage();
      } else {
        toast.error("Failed to update XONDER settings. Please verify your Account ID.");
      }
    } catch (error: any) {
      console.error("Failed to update settings:", error);
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "Could not connect XONDER account. Please check your ID and try again.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading XONDER settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="XONDER Settings"
        description="Connect your XONDER account for enhanced features"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Zap size={18} className="text-white" />
            XONDER Integration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1 ">
          <div className="space-y-5">
            {/* Information Section */}
            <div>
              <SectionHeader title="Account Information" />
              {/* Info Box */}
              <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex gap-3">
                  <Info
                    size={20}
                    className="text-[#885ABB] flex-shrink-0 mt-0.5"
                  />
                  <div className="space-y-2">
                    <p className="text-sm text-purple-900 font-medium">
                      About XONDER Integration
                    </p>
                    <p className="text-sm text-purple-700 leading-relaxed">
                      Connect your XONDER account to unlock advanced features
                      and seamless integration. Your account ID can be found in
                      your XONDER dashboard settings.
                    </p>
                    <p className="text-xs text-purple-600">
                      Need help? Visit the XONDER documentation or contact
                      support.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account ID Input Section */}
            <div>
              <SectionHeader title="Account Configuration" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  XONDER Account ID <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={xonderId}
                  onChange={(e) => setXonderId(e.target.value)}
                  placeholder="Enter your XONDER account ID"
                />
                <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                  <Info size={12} />
                  This ID is used to link your account with XONDER services (stored in Stripe ID field)
                </p>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={!xonderId.trim() || saving}
                className={`px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 ${!xonderId.trim() || saving
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#885ABB] hover:bg-[#7748A4] text-white"
                  }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default XONDERSettings;
